import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import {
  LogOut, Menu, Loader2, Plus, Trash2, Upload, RefreshCw,
  LayoutDashboard, Workflow, BarChart3, Activity, Store, Shield,
  Building2, FileText, Search, Database, Zap, ChevronRight,
  CheckCircle2, AlertTriangle, Globe, MapPin, Cpu
} from 'lucide-react';
import { HSLogo } from '@/components/HSLogo';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Knowledge = () => {
  const { bizId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [tab, setTab] = useState('files');
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [suggestions, setSuggestions] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [uploadForm, setUploadForm] = useState({ filename: '', content: '', source_type: 'sop' });
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [notesForm, setNotesForm] = useState('');
  const [regionForm, setRegionForm] = useState({ state: '', city: '' });
  const [regLookup, setRegLookup] = useState(null);

  useEffect(() => { fetchProfile(); }, [bizId]);

  const fetchProfile = async () => {
    try {
      const res = await axios.get(`${API}/knowledge/${bizId}`);
      setProfile(res.data);
      setNotesForm(res.data.onboarding_notes || '');
      setRegionForm(res.data.local_region || { state: '', city: '' });
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleUpload = async () => {
    if (!uploadForm.filename || !uploadForm.content) return toast.error('Filename and content required');
    try {
      await axios.post(`${API}/knowledge/files/upload`, { business_id: bizId, ...uploadForm });
      toast.success('File uploaded and indexed');
      setShowUpload(false);
      setUploadForm({ filename: '', content: '', source_type: 'sop' });
      fetchProfile();
    } catch { toast.error('Upload failed'); }
  };

  const handleDeleteFile = async (fileId) => {
    try {
      await axios.delete(`${API}/knowledge/files/${fileId}`);
      toast.success('File deleted');
      fetchProfile();
    } catch { toast.error('Delete failed'); }
  };

  const handleSaveNotes = async () => {
    try {
      await axios.patch(`${API}/knowledge/${bizId}`, { onboarding_notes: notesForm, local_region: regionForm });
      toast.success('Saved');
      fetchProfile();
    } catch { toast.error('Failed'); }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    try {
      const res = await axios.post(`${API}/knowledge/search/${bizId}?query=${encodeURIComponent(searchQuery)}`);
      setSearchResults(res.data);
    } catch { toast.error('Search failed'); }
  };

  const handleRegLookup = async () => {
    if (!regionForm.state) return toast.error('Enter a state');
    try {
      const res = await axios.post(`${API}/knowledge/regulations/lookup?state=${regionForm.state}&project_type=general`);
      setRegLookup(res.data);
    } catch { toast.error('Lookup failed'); }
  };

  const navItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
    { icon: Building2, label: 'Businesses', path: '/businesses' },
    { icon: Workflow, label: 'Workflow Builder', path: '/workflow' },
    { icon: Activity, label: 'Agent Monitor', path: '/agents' },
  ];

  return (
    <div className="min-h-screen bg-[#050505] flex">
      <div className="noise-overlay" />
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-[#0a0a0a] border-r border-white/5 transform transition-transform lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          <div className="p-6 border-b border-white/5"><Link to="/" className="flex items-center gap-2"><HSLogo size={32} /><span className="font-black text-lg tracking-tight">HumanScaled</span></Link></div>
          <nav className="flex-1 p-4 space-y-1">
            {navItems.map(item => { const Icon = item.icon; const isActive = location.pathname === item.path; return (<Link key={item.path} to={item.path} className={`flex items-center gap-3 px-4 py-3 rounded-sm transition-colors ${isActive ? 'bg-emerald-500/10 text-emerald-400 border-l-2 border-emerald-500' : 'text-muted-foreground hover:text-white hover:bg-white/5'}`}><Icon className="w-5 h-5" />{item.label}</Link>); })}
          </nav>
          <div className="p-4 border-t border-white/5"><Button variant="ghost" onClick={() => { logout(); navigate('/'); }} className="w-full justify-start text-muted-foreground hover:text-white"><LogOut className="w-4 h-4 mr-2" /> Sign Out</Button></div>
        </div>
      </aside>
      {sidebarOpen && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      <main className="flex-1 min-h-screen">
        <header className="sticky top-0 z-30 bg-[#050505]/80 backdrop-blur-md border-b border-white/5">
          <div className="flex items-center justify-between h-16 px-6">
            <div className="flex items-center gap-4">
              <button onClick={() => setSidebarOpen(true)} className="lg:hidden p-2 text-muted-foreground hover:text-white"><Menu className="w-6 h-6" /></button>
              <button onClick={() => navigate('/businesses')} className="text-muted-foreground hover:text-white"><ChevronRight className="w-4 h-4 rotate-180" /></button>
              <h1 className="text-lg font-bold" data-testid="knowledge-title">Company Knowledge</h1>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex bg-[#0a0a0a] border border-white/10 rounded-sm overflow-hidden">
                {[{ key: 'files', label: 'FILES' }, { key: 'notes', label: 'NOTES' }, { key: 'search', label: 'SEARCH' }, { key: 'regulations', label: 'CODES' }].map(t => (
                  <button key={t.key} onClick={() => setTab(t.key)} className={`px-3 py-1.5 text-xs font-mono ${tab === t.key ? 'bg-emerald-500/20 text-emerald-400' : 'text-muted-foreground hover:text-white'}`} data-testid={`tab-${t.key}`}>{t.label}</button>
                ))}
              </div>
              <Button onClick={() => setShowUpload(true)} className="bg-emerald-500 text-black hover:bg-emerald-400 font-bold rounded-sm h-9 text-sm" data-testid="upload-btn"><Upload className="w-4 h-4 mr-1" /> Upload</Button>
            </div>
          </div>
        </header>

        <div className="p-6 lg:p-8">
          {loading ? <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-emerald-500" /></div> : (
            <>
              {/* Stats */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="bg-[#0a0a0a] border border-white/5 p-4"><p className="text-[10px] text-muted-foreground uppercase">Files</p><p className="font-mono text-2xl font-bold">{profile?.files?.length || 0}</p></div>
                <div className="bg-[#0a0a0a] border border-white/5 p-4"><p className="text-[10px] text-muted-foreground uppercase">Chunks Indexed</p><p className="font-mono text-2xl font-bold text-emerald-400">{profile?.chunks_indexed || 0}</p></div>
                <div className="bg-[#0a0a0a] border border-white/5 p-4"><p className="text-[10px] text-muted-foreground uppercase">Region</p><p className="font-mono text-lg font-bold">{profile?.local_region?.state || 'Not set'}</p></div>
              </div>

              {/* FILES TAB */}
              {tab === 'files' && (
                <div className="space-y-3" data-testid="files-tab">
                  {(profile?.files || []).length === 0 ? (
                    <div className="bg-[#0a0a0a] border border-white/5 p-12 text-center">
                      <Database className="w-10 h-10 mx-auto mb-3 text-muted-foreground opacity-30" />
                      <p className="font-bold mb-1">No knowledge files</p>
                      <p className="text-sm text-muted-foreground mb-4">Upload SOPs, documents, and data files to build your company knowledge layer.</p>
                      <Button onClick={() => setShowUpload(true)} className="bg-emerald-500 text-black hover:bg-emerald-400 rounded-sm">Upload File</Button>
                    </div>
                  ) : (profile?.files || []).map(f => (
                    <div key={f.id} className="bg-[#0a0a0a] border border-white/5 p-4 flex items-center justify-between group" data-testid={`file-${f.id}`}>
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-muted-foreground" />
                        <div>
                          <p className="font-medium text-sm">{f.filename}</p>
                          <div className="flex gap-3 text-[10px] text-muted-foreground">
                            <span className="uppercase">{f.source_type}</span>
                            <span>{f.size_bytes} bytes</span>
                            <span>Indexed: {new Date(f.indexed_at).toLocaleDateString()}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-1.5 opacity-0 group-hover:opacity-100 transition-all">
                        <Button onClick={() => axios.post(`${API}/knowledge/files/${f.id}/reindex`).then(() => { toast.success('Re-indexed'); fetchProfile(); })} variant="outline" size="sm" className="border-white/10 rounded-sm h-7 text-[10px] px-2"><RefreshCw className="w-3 h-3" /></Button>
                        <Button onClick={() => handleDeleteFile(f.id)} variant="outline" size="sm" className="border-red-500/20 text-red-400 rounded-sm h-7 text-[10px] px-2"><Trash2 className="w-3 h-3" /></Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* NOTES TAB */}
              {tab === 'notes' && (
                <div className="space-y-4 max-w-2xl" data-testid="notes-tab">
                  <div>
                    <label className="text-xs text-muted-foreground uppercase tracking-wider mb-2 block">Onboarding Notes</label>
                    <Textarea value={notesForm} onChange={e => setNotesForm(e.target.value)} placeholder="Business preferences, special requirements, key contacts..." className="bg-[#0a0a0a] border-white/10 rounded-sm min-h-[120px]" data-testid="notes-input" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><label className="text-xs text-muted-foreground uppercase mb-2 block">State</label><Input value={regionForm.state} onChange={e => setRegionForm({ ...regionForm, state: e.target.value })} placeholder="TX" className="bg-[#0a0a0a] border-white/10 rounded-sm" data-testid="state-input" /></div>
                    <div><label className="text-xs text-muted-foreground uppercase mb-2 block">City</label><Input value={regionForm.city} onChange={e => setRegionForm({ ...regionForm, city: e.target.value })} placeholder="Houston" className="bg-[#0a0a0a] border-white/10 rounded-sm" /></div>
                  </div>
                  <Button onClick={handleSaveNotes} className="bg-emerald-500 text-black hover:bg-emerald-400 rounded-sm" data-testid="save-notes-btn">Save Notes & Region</Button>
                </div>
              )}

              {/* SEARCH TAB */}
              {tab === 'search' && (
                <div className="space-y-4 max-w-2xl" data-testid="search-tab">
                  <div className="flex gap-2">
                    <Input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSearch()} placeholder="Search knowledge base..." className="flex-1 bg-[#0a0a0a] border-white/10 rounded-sm" data-testid="search-input" />
                    <Button onClick={handleSearch} className="bg-emerald-500 text-black hover:bg-emerald-400 rounded-sm"><Search className="w-4 h-4" /></Button>
                  </div>
                  {searchResults && (
                    <div className="space-y-2">
                      <p className="text-xs text-muted-foreground">{searchResults.count} results for "{searchResults.query}"</p>
                      {searchResults.results.map((r, i) => (
                        <div key={i} className="bg-[#0a0a0a] border border-white/5 p-3">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-[10px] px-1.5 py-0.5 bg-white/5 text-muted-foreground uppercase">{r.source_type}</span>
                            <span className="text-xs text-muted-foreground">{r.filename}</span>
                          </div>
                          <p className="text-xs text-white/80">{r.content.slice(0, 200)}...</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* REGULATIONS TAB */}
              {tab === 'regulations' && (
                <div className="space-y-4 max-w-2xl" data-testid="regulations-tab">
                  <div className="flex gap-2">
                    <Input value={regionForm.state} onChange={e => setRegionForm({ ...regionForm, state: e.target.value })} placeholder="State (e.g. TX)" className="w-24 bg-[#0a0a0a] border-white/10 rounded-sm" />
                    <Button onClick={handleRegLookup} className="bg-emerald-500 text-black hover:bg-emerald-400 rounded-sm"><MapPin className="w-4 h-4 mr-1" /> Look Up Codes</Button>
                  </div>
                  {regLookup && (
                    <div className="bg-[#0a0a0a] border border-emerald-500/20 p-5" data-testid="reg-result">
                      <h3 className="font-bold mb-2">{regLookup.state} — Building Codes</h3>
                      <div className="space-y-2 text-sm text-muted-foreground">
                        {regLookup.building_codes && <p><span className="text-white">Codes:</span> {regLookup.building_codes}</p>}
                        {regLookup.wind_zone && <p><span className="text-white">Wind Zone:</span> {regLookup.wind_zone}</p>}
                        {regLookup.notes && <p><span className="text-white">Notes:</span> {regLookup.notes}</p>}
                        <p>{regLookup.summary}</p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </main>

      {/* Upload Modal */}
      <Dialog open={showUpload} onOpenChange={setShowUpload}>
        <DialogContent className="bg-[#0a0a0a] border-white/10 max-w-md">
          <DialogHeader><DialogTitle>Upload Knowledge File</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-3">
            <Input value={uploadForm.filename} onChange={e => setUploadForm({ ...uploadForm, filename: e.target.value })} placeholder="filename.txt" className="bg-black/50 border-white/10 rounded-sm" data-testid="upload-filename" />
            <select value={uploadForm.source_type} onChange={e => setUploadForm({ ...uploadForm, source_type: e.target.value })} className="w-full bg-black/50 border border-white/10 rounded-sm p-2 text-sm text-white">
              <option value="sop">SOP</option>
              <option value="claim">Insurance Claim</option>
              <option value="dataset">Dataset</option>
              <option value="document">Document</option>
            </select>
            <Textarea value={uploadForm.content} onChange={e => setUploadForm({ ...uploadForm, content: e.target.value })} placeholder="Paste file content..." className="bg-black/50 border-white/10 rounded-sm min-h-[120px]" data-testid="upload-content" />
            <Button onClick={handleUpload} className="w-full bg-emerald-500 text-black hover:bg-emerald-400 font-bold rounded-sm" data-testid="upload-submit">Upload & Index</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Knowledge;
